// =====================================================================
//  Cotización progresiva por olas — Backend como vía principal
//  Autor: JORGE ALBERTO BAEZ PEREZ
//  Huella SHA-256: 43f31a1049af791677048c8a2bb0f5d19c081b4a267bf51a27e6c25b270cc69d
//
//  Las funciones backend (quoteShipments / publicQuotesShipments) llaman a
//  los agregadores externos vía fetch — NO usan InvokeLLM ni créditos de
//  integración de Base44, así que funcionan incluso con créditos agotados.
//  El fallback client-side (tarifas locales + Envia CORS) solo activa si
//  TODAS las olas del backend fallan (cero tarifas), como último recurso.
// =====================================================================
import { dropDisabledRates, publishDisabledSources } from "@/lib/disabledSources";

import { base44 } from "@/api/base44Client";
import { quoteAllWithUserTariffs } from "@/lib/quoteEngine";
import { clientQuoteAggregators } from "@/lib/clientAggregators";
import { quoteViaWorker } from "@/lib/quoteWorkerConfig";

// Agregadores que el backend no puede alcanzar (bloqueo de IP/WAF o deuda) y
// que el Worker de Cloudflare sí puede cotizar. El worker corre en paralelo
// y rellena SOLO las tarifas de estos agregadores cuando el backend devolvió
// cero para ellos (sin duplicar los que el backend sí pudo cotizar).
const BLOCKED_AGGS = ["srsimi", "interpack", "skypractico", "slenvios"];

// Caché de cotizaciones en memoria (corta TTL) + dedupe de llamadas en vuelo.
const _quoteCache = new Map();
const _quoteInflight = new Map();
const QUOTE_CACHE_TTL = 60000;
function stableSig(payload) {
  try { return JSON.stringify(payload, Object.keys(payload).sort()); }
  catch { return JSON.stringify(payload); }
}
function cachedInvoke(fn, payload) {
  const key = fn + "\u0001" + stableSig(payload);
  const now = Date.now();
  const hit = _quoteCache.get(key);
  if (hit && now - hit.ts < QUOTE_CACHE_TTL) return Promise.resolve(hit.res);
  const existing = _quoteInflight.get(key);
  if (existing) return existing;
  const p = base44.functions.invoke(fn, payload)
    .then((res) => { _quoteCache.set(key, { res, ts: now }); _quoteInflight.delete(key); return res; })
    .catch((e) => { _quoteInflight.delete(key); throw e; });
  _quoteInflight.set(key, p);
  return p;
}

const WAVES = [
  ["pak2go"],                                    // EPAK ~0.2s → primer resultado
  ["interno", "enviot2", "genienvios"],          // ~0.8s (convenios internos)
  ["interpack", "skypractico"],                  // ~6s
  ["elogicya", "shipwoo", "multipaq"],           // ~11s
  ["envia"],                                     // Tendencys ~18s (ola propia)
  ["mydhl"],                                     // SGM ~18s (ola propia)
  ["directo", "sigomx"]                          // ~21s (los más lentos restantes)
];

export function fetchQuotesProgressive(functionName, rawPayload, { onRates, onSrSimiDone, onError, onComplete, maxWaitMs = 30000, turnstilePass = null, webQuote = false }) {
  let cancelled = false;
  // Cotizador web: cada ola lleva el pase anti-bot que valida el servidor.
  const payload = rawPayload;
  const backendPayload = webQuote ? { ...rawPayload, web_quote: true, turnstile_pass: turnstilePass } : rawPayload;
  // Convenios apagados por el admin (unión de lo que reporta cada ola del backend).
  const disabledSet = new Set();
  const noteDisabled = (res) => {
    for (const k of (res?.data?.disabledSources || [])) disabledSet.add(k);
    publishDisabledSources([...disabledSet]);
  };
  let srDone = false;
  let fallbackFired = false;
  let completeFired = false;
  let fallbackDone = false;
  const collected = {};
  const dimCollected = {};
  // Contador de olas completadas (éxito o fallo). El fallback client-side
  // solo activa si TODAS las olas fallan y devuelven cero tarifas.
  let wavesDone = 0;
  const totalWaves = WAVES.length + 1; // +1 por SrSimi (el Worker se rastrea con workerDone)

  // Tarifas del Worker para agregadores bloqueados del backend (srsimi por WAF,
  // interpack/skypractico/slenvios por deuda en Skydropx). Se almacenan aparte y
  // se inyectan en emit() SOLO para los agregadores donde el backend devolvió
  // cero tarifas — así no duplica los que el backend sí pudo cotizar.
  let workerRates = [];
  let workerDone = false;
  quoteViaWorker({ ...payload, only: BLOCKED_AGGS }).then((rates) => {
    if (cancelled) return;
    workerRates = Array.isArray(rates) ? rates : [];
    workerDone = true;
    emit();
    maybeComplete();
  }).catch(() => { workerDone = true; maybeComplete(); });

  const maybeComplete = () => {
    if (completeFired || cancelled) return;
    if (wavesDone < totalWaves || !workerDone) return;
    if (!Object.values(collected).flat().length && !workerRates.length && !fallbackDone) {
      tryClientFallback();
      return;
    }
    completeFired = true;
    clearTimeout(timer);
    if (onComplete) onComplete();
  };

  const fallbackLocalQuote = async (p) => {
    try {
      const [tariffsRes, utRes] = await Promise.all([
        base44.entities.CustomTariff.list("-created_date", 500),
        base44.entities.UserTariff.list("-created_date", 500).catch(() => [])
      ]);
      const tariffs = (tariffsRes || []).filter(t => t.enabled);
      const userTariffs = (utRes || []).filter(u => u.active !== false);
      const dims = { length: Number(p.length_cm) || 0, width: Number(p.width_cm) || 0, height: Number(p.height_cm) || 0 };
      // BHGC se cotiza solo en el backend (respeta el apagado del admin): el
      // fallback local jamás emite tarifas del carrier BHGC como "interno".
      const rates = quoteAllWithUserTariffs(tariffs, userTariffs, p.origin_cp, p.dest_cp, Number(p.weight_kg) || 1, dims)
        .filter(r => r.carrier !== "BHGC");
      return rates.map(r => {
        const cost = Number(r.total_cost) || 0;
        const margin = Number((cost * 0.048 + 2).toFixed(2));
        return {
          ...r, source: "interno", aggregator: "directo", direct_api: false,
          provider_cost: Number(cost.toFixed(2)), platform_margin: margin, total_cost: Number((cost + margin).toFixed(2))
        };
      });
    } catch { return []; }
  };

  // Fallback client-side de último recurso: solo se activa si TODAS las olas
  // del backend fallaron (cero tarifas en collected). Dispara el Worker con
  // TODOS los agregadores que soporta (no solo los bloqueados) + tarifas
  // locales (CustomTariff + UserTariff) + Envia/Tendencys directo (CORS ✓).
  // Así, aunque el "cerebro" Base44 (cuota de invocación de funciones) caiga,
  // el usuario sigue viendo tarifas de todos los carriers y agregadores.
  const tryClientFallback = () => {
    if (fallbackFired || cancelled) return;
    const allSoFar = Object.values(collected).flat();
    if (allSoFar.length > 0 || workerRates.length > 0) return; // ya tenemos tarifas (backend o worker)
    if (wavesDone < totalWaves || !workerDone) return; // aún quedan olas o el worker por completar
    fallbackFired = true;
    // Worker completo (todos los agregadores que soporta) — no se limita a
    // BLOCKED_AGGS porque aquí el backend entero cayó, no solo un agregador.
    const workerFull = quoteViaWorker(payload).catch(() => []);
    Promise.all([
      fallbackLocalQuote(payload),
      clientQuoteAggregators(payload),
      workerFull
    ]).then(([localRates, aggRates, workerFullRates]) => {
      if (cancelled) return;
      // El fallback no consulta al admin: se filtra con lo ya conocido y, además,
      // las tarifas BHGC locales solo se admiten si el backend no reportó BHGC apagado.
      const all = dropDisabledRates([...localRates, ...aggRates, ...(Array.isArray(workerFullRates) ? workerFullRates : [])], disabledSet);
      if (all.length) onRates(all, [], [...disabledSet]);
      else if (onError) onError(new Error("Sin tarifas del backend ni del cliente"));
    }).catch(() => {
      if (!cancelled && onError) onError(new Error("Sin tarifas disponibles"));
    }).finally(() => {
      fallbackDone = true;
      maybeComplete();
    });
    if (onSrSimiDone) onSrSimiDone();
  };

  const emit = () => {
    if (cancelled) return;
    const all = Object.values(collected).flat();
    const hasRealDhl = all.some(r => r.carrier === "DHL" && r.direct_api);
    const noDemoDhl = hasRealDhl
      ? all.filter(r => !(r.carrier === "DHL" && !r.direct_api && r.source === "interno"))
      : all;
    const seen = new Set();
    let final = noDemoDhl.filter(r => {
      const k = `${r.carrier}|${r.service || ""}|${r.source || ""}|${r.aggregator || ""}|${r.total_cost}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    // Inyecta tarifas del Worker para agregadores bloqueados donde el backend
    // devolvió cero (srsimi/interpack/skypractico/slenvios). Si el backend sí
    // cotizó ese agregador, se omite el Worker para no duplicar.
    const backendSources = new Set(final.map(r => r.source || r.aggregator).filter(Boolean));
    const workerToAdd = workerRates.filter(r => {
      const s = r.source || r.aggregator;
      return BLOCKED_AGGS.includes(s) && !backendSources.has(s);
    });
    if (workerToAdd.length) final = [...final, ...workerToAdd];
    // Candado: nada de un convenio apagado por el admin llega al cotizador.
    final = dropDisabledRates(final, disabledSet);

    // Agrega las tarifas ocultas por límites de dimensiones reportadas por cada
    // ola del backend (res.data.dimFiltered) y las pasa al cotizador para que
    // avise al usuario cuáles carriers se ocultaron y por qué.
    const dimSeen = new Set();
    const dimFiltered = Object.values(dimCollected).flat().filter(d => {
      if (!d || !d.carrier) return false;
      const k = `${d.carrier}|${d.source || ""}|${d.service || ""}|${d.reason || ""}`;
      if (dimSeen.has(k)) return false;
      dimSeen.add(k);
      return true;
    });
    onRates(final, dimFiltered, [...disabledSet]);
  };

  const fireWave = (key, onlyAggs) => {
    cachedInvoke(functionName, { ...backendPayload, only_aggs: onlyAggs })
      .then((res) => {
        if (cancelled) return;
        noteDisabled(res);
        collected[key] = res.data?.rates || [];
        dimCollected[key] = res.data?.dimFiltered || [];
        wavesDone++;
        emit();
        maybeComplete();
      })
      .catch(() => {
        if (cancelled) return;
        collected[key] = [];
        wavesDone++;
        emit();
        maybeComplete();
        tryClientFallback();
      });
  };

  WAVES.forEach((aggs, i) => fireWave("w" + i, aggs));

  // SrSimi: su propia ola.
  const finishSr = () => { if (!srDone) { srDone = true; if (onSrSimiDone) onSrSimiDone(); } };
  cachedInvoke(functionName, { ...backendPayload, srsimi_only: true })
    .then((res) => {
      if (cancelled) return;
      noteDisabled(res);
      collected.srsimi = res.data?.rates || [];
      dimCollected.srsimi = res.data?.dimFiltered || [];
      wavesDone++;
      finishSr();
      emit();
      maybeComplete();
    })
    .catch(() => {
      if (cancelled) return;
      collected.srsimi = [];
      wavesDone++;
      finishSr();
      maybeComplete();
      tryClientFallback();
    });

  const timer = setTimeout(() => {
    if (cancelled) return;
    finishSr();
    emit();
    // Las respuestas pendientes siguen llegando: no declarar finalizada una
    // búsqueda ni iniciar un fallback antes de que terminen las olas reales.
    maybeComplete();
  }, maxWaitMs);

  return () => { cancelled = true; clearTimeout(timer); };
}